import {
  type MeetingFormat,
  type MeetingSlot,
  type ViewContext,
  addDays,
  canJoinMeeting,
  shortDate,
  timeOf,
  todayIso,
  useAvailableSlots,
  useCurrentContext,
  useMeetingActions,
  useMeetingHosts,
  useMeetings,
} from '@shkola/api';
import { Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNow } from '../components/AlarmBanner';
import {
  Button,
  EmptyState,
  ErrorText,
  Field,
  Modal,
  Segmented,
  Select,
  Spinner,
} from '../components/ui';
import {
  Block,
  EventArt,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  grad,
  pillClass,
} from '../design/forms';
import { CalendarDotsIcon, PlusIcon, VideoCameraIcon } from '../design/icons';
import { readEnv } from '../env';
import { capitalize } from '../student/home/model';

/*
 * «Зустрічі» in «Форми 2» (28.10). The staff: the title on the paper with «Години прийому» beside it,
 * then one white block of the days — each day its slots as small cards: a booked one in the soft butter
 * of a meeting with its picture quiet behind, a free one on the paper. The family: «Мої зустрічі» as the
 * same cards on top (one short line when there are none — no half-empty column), then who to book.
 */

const kyivDate = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Kyiv' }).format(new Date(iso));

/** «Пн, 2 лист.» */
const dayTitle = (day: string) => capitalize(shortDate(day));

/** Slots grouped by their day, in order. */
function byDay<T extends { startsAt: string }>(slots: readonly T[]): Array<[string, T[]]> {
  const days = new Map<string, T[]>();
  for (const s of slots) {
    const day = kyivDate(s.startsAt);
    days.set(day, [...(days.get(day) ?? []), s]);
  }
  return [...days];
}

/** A label: a soft rectangle; white on a coloured card. */
function Label({
  tone,
  white = false,
  children,
}: {
  tone: Grad;
  white?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className="inline-flex h-6 max-w-full shrink-0 items-center truncate rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={
        white
          ? { backgroundColor: 'rgb(255 255 255 / 0.8)', color: GRAD[tone].ink }
          : { backgroundImage: grad(tone), color: GRAD[tone].ink }
      }
    >
      {children}
    </span>
  );
}

function FormatLabel({
  format,
  place,
  white = false,
}: {
  format: MeetingFormat;
  place: string | null;
  white?: boolean;
}) {
  const { t } = useTranslation();
  return format === 'online' ? (
    <Label tone="lavender" white={white}>
      {t('meetings.online')}
    </Label>
  ) : (
    <Label tone="stone" white={white}>
      {place ?? t('meetings.inPerson')}
    </Label>
  );
}

function JoinLink({ slot }: { slot: MeetingSlot }) {
  const { t } = useTranslation();
  const now = useNow(30_000);
  if (!canJoinMeeting(slot, now)) return null;
  return (
    <Link
      to="/meeting/$meetingId"
      params={{ meetingId: slot.id }}
      className={pillClass('dark', 'sm')}
    >
      {t('meetings.join')}
      <PillIcon icon={VideoCameraIcon} size="sm" />
    </Link>
  );
}

/** Cancel or remove: a quiet word, not a button that calls. */
function QuietAction({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-9 rounded-full px-2 text-[13px] font-medium underline-offset-2 hover:underline"
      style={{ color: INK_SOFT }}
    >
      {children}
    </button>
  );
}

/** One meeting as a small card: booked — the butter of a meeting with its picture; free — the paper. */
function SlotCard({
  slot,
  title,
  badge,
  lines,
  actions,
}: {
  slot: MeetingSlot;
  title: ReactNode;
  /** beside the time: the format */
  badge: ReactNode;
  lines?: ReactNode;
  actions: ReactNode;
}) {
  const booked = Boolean(slot.guestName);
  return (
    <li
      className={`relative isolate flex min-w-0 flex-col overflow-hidden rounded-[22px] p-4 ${booked ? 'min-h-[136px]' : ''}`}
      style={
        booked
          ? { backgroundImage: grad('butter'), color: GRAD.butter.ink }
          : { backgroundColor: PAPER, color: INK_SOFT }
      }
    >
      {booked && (
        <EventArt
          kind="meeting"
          tone="butter"
          size={120}
          ghost
          className="pointer-events-none absolute -right-5 -bottom-6 -z-10"
        />
      )}
      <div className="flex min-w-0 items-start gap-2">
        <span
          className="min-w-0 flex-1 font-display text-[22px] leading-7 font-light tracking-[-0.02em] tabular-nums"
          style={{ color: INK }}
        >
          {title}
        </span>
        <span className="flex min-w-0 shrink pt-0.5">{badge}</span>
      </div>
      {lines && <div className="mt-1.5 flex min-w-0 flex-col gap-1.5">{lines}</div>}
      <div className="mt-auto flex flex-wrap items-center gap-1 pt-2">{actions}</div>
    </li>
  );
}

function OfficeHours({ schoolId, onClose }: { schoolId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const create = useMeetingActions().createSlots;
  const [date, setDate] = useState(addDays(todayIso(), 1));
  const [from, setFrom] = useState('15:00');
  const [to, setTo] = useState('16:00');
  const [minutes, setMinutes] = useState(15);
  const [format, setFormat] = useState<MeetingFormat>('online');
  const [place, setPlace] = useState('');
  return (
    <Modal title={t('meetings.addHours')} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void create
            .mutateAsync({ schoolId, date, from, to, minutes, format, place })
            .then(onClose);
        }}
      >
        <Field
          id="date"
          type="date"
          label={t('meetings.date')}
          value={date}
          min={todayIso()}
          onChange={(e) => setDate(e.target.value)}
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            id="from"
            type="time"
            label={t('meetings.from')}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            required
          />
          <Field
            id="to"
            type="time"
            label={t('meetings.to')}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            required
          />
        </div>
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
            {t('meetings.length')}
          </span>
          <Select
            className="h-11 w-full"
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
          >
            {[10, 15, 20, 30].map((m) => (
              <option key={m} value={m}>
                {t('meetings.minutes', { count: m })}
              </option>
            ))}
          </Select>
        </label>
        <Segmented
          value={format}
          onChange={setFormat}
          options={[
            { value: 'online', label: t('meetings.online') },
            { value: 'in_person', label: t('meetings.inPerson') },
          ]}
        />
        {format === 'in_person' && (
          <Field
            id="place"
            label={t('meetings.place')}
            placeholder={t('meetings.placeHint')}
            maxLength={100}
            value={place}
            onChange={(e) => setPlace(e.target.value)}
          />
        )}
        {create.isError && <ErrorText>{t('meetings.wrongHours')}</ErrorText>}
        <Button type="submit" disabled={create.isPending}>
          {t('meetings.publish')}
        </Button>
      </form>
    </Modal>
  );
}

/** Nothing yet: words on the soft butter of a meeting with its picture behind. */
function EmptyCard({ children }: { children: ReactNode }) {
  return (
    <div
      className="relative isolate flex min-h-[148px] items-center overflow-hidden rounded-[28px] p-6 pr-40"
      style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
    >
      <EventArt
        kind="meeting"
        tone="butter"
        size={150}
        ghost
        className="pointer-events-none absolute top-1/2 right-6 -z-10 -translate-y-1/2"
      />
      <p
        className="max-w-[560px] font-display text-[22px] leading-[30px] font-light tracking-[-0.02em]"
        style={{ color: INK }}
      >
        {children}
      </p>
    </div>
  );
}

function StaffMeetings({
  ctx,
}: {
  ctx: Extract<ViewContext, { kind: 'teacher' | 'admin' | 'psychologist' }>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slots = useMeetings({ hostId: ctx.memberId });
  const actions = useMeetingActions();
  const [adding, setAdding] = useState(false);
  const demo =
    readEnv(import.meta.env as Record<string, string | undefined>).appEnv !== 'production';
  const days = byDay(slots.data ?? []);
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('meetings.titleStaff')}>
        {demo && (
          <button
            type="button"
            disabled={actions.startDemo.isPending}
            className={`${pillClass('light')} disabled:opacity-50`}
            onClick={() =>
              void actions.startDemo
                .mutateAsync()
                .then((meetingId) => navigate({ to: '/meeting/$meetingId', params: { meetingId } }))
            }
          >
            {t('meetings.demoNow')}
            <PillIcon icon={VideoCameraIcon} />
          </button>
        )}
        <button type="button" className={pillClass('dark')} onClick={() => setAdding(true)}>
          {t('meetings.addHours')}
          <PillIcon icon={PlusIcon} />
        </button>
      </PageHead>
      {slots.isPending ? (
        <Spinner />
      ) : days.length === 0 ? (
        <EmptyCard>{t('meetings.noHours')}</EmptyCard>
      ) : (
        <Block className="gap-6">
          {days.map(([day, list]) => (
            <section key={day} className="min-w-0">
              <Head
                title={dayTitle(day)}
                note={t('meetings.booked', {
                  booked: list.filter((s) => s.guestName).length,
                  total: list.length,
                })}
              />
              <ul className="-mt-2 grid grid-cols-[repeat(auto-fill,minmax(230px,1fr))] gap-2">
                {list.map((s) => (
                  <SlotCard
                    key={s.id}
                    slot={s}
                    title={`${timeOf(s.startsAt)}–${timeOf(s.endsAt)}`}
                    badge={
                      <FormatLabel format={s.format} place={s.place} white={Boolean(s.guestName)} />
                    }
                    {...(s.guestName && {
                      lines: (
                        <span className="text-[13.5px] leading-[19px]">
                          <span className="font-medium" style={{ color: INK }}>
                            {s.guestName}
                          </span>
                          <span className="block" style={{ color: INK_SOFT }}>
                            {t('meetings.about', { name: s.studentName ?? '' })}
                            {s.topic ? ` · ${s.topic}` : ''}
                          </span>
                        </span>
                      ),
                    })}
                    actions={
                      <>
                        {!s.guestName && (
                          <span className="mr-auto text-[13.5px]" style={{ color: INK_FAINT }}>
                            {t('meetings.free')}
                          </span>
                        )}
                        <JoinLink slot={s} />
                        {Date.parse(s.startsAt) > Date.now() && (
                          <QuietAction onClick={() => actions.cancel.mutate(s.id)}>
                            {s.guestName ? t('meetings.cancel') : t('meetings.remove')}
                          </QuietAction>
                        )}
                      </>
                    }
                  />
                ))}
              </ul>
            </section>
          ))}
        </Block>
      )}
      {adding && <OfficeHours schoolId={ctx.schoolId} onClose={() => setAdding(false)} />}
    </div>
  );
}

function BookSlot({
  ctx,
  host,
  onClose,
}: {
  ctx: Extract<ViewContext, { kind: 'guardian' }>;
  host: { member_id: string; first_name: string; last_name: string; middle_name: string | null };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const free = useAvailableSlots(host.member_id);
  const book = useMeetingActions().book;
  const [slot, setSlot] = useState<string>();
  const [topic, setTopic] = useState('');
  const days = byDay(
    (free.data ?? []).map((s) => ({ ...s, startsAt: s.starts_at, endsAt: s.ends_at })),
  );
  return (
    <Modal
      title={t('meetings.bookWith', {
        name: `${host.first_name} ${host.middle_name ?? host.last_name}`,
      })}
      onClose={onClose}
    >
      {free.isPending ? (
        <Spinner />
      ) : days.length === 0 ? (
        <EmptyState>{t('meetings.noFree')}</EmptyState>
      ) : (
        <div className="max-h-72 space-y-4 overflow-y-auto">
          {days.map(([day, list]) => (
            <div key={day}>
              <p className="mb-2 text-[13px] font-medium" style={{ color: INK_SOFT }}>
                {dayTitle(day)}
              </p>
              <div className="flex flex-wrap gap-2">
                {list.map((s) => {
                  const on = slot === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setSlot(s.id)}
                      className={`inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-[14px] font-medium tabular-nums transition-colors ${
                        on
                          ? 'bg-[var(--sh-dark)] text-white'
                          : 'bg-white text-[var(--sh-ink)] ring-1 ring-[#E4E0D7] ring-inset hover:bg-[#FAF9F6]'
                      }`}
                    >
                      {timeOf(s.startsAt)}
                      <span className="text-[12px] font-normal opacity-70">
                        {s.format === 'online' ? t('meetings.online') : (s.place ?? '')}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4">
        <Field
          id="topic"
          label={t('meetings.topic')}
          placeholder={t('meetings.topicHint')}
          maxLength={300}
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />
      </div>
      {book.isError && <ErrorText>{t('meetings.bookFailed')}</ErrorText>}
      <Button
        className="mt-4"
        disabled={!slot || book.isPending}
        onClick={() =>
          slot &&
          void book
            .mutateAsync({ slotId: slot, studentId: ctx.child.memberId, topic })
            .then(onClose)
        }
      >
        {t('meetings.book')}
      </Button>
    </Modal>
  );
}

/** The first letters of a name in a soft circle. */
function Initials({ name, tone }: { name: string; tone: Grad }) {
  const letters = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      aria-hidden
      className="flex size-10 shrink-0 items-center justify-center rounded-full text-[14px] font-medium"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {letters}
    </span>
  );
}

function FamilyMeetings({ ctx }: { ctx: Extract<ViewContext, { kind: 'guardian' }> }) {
  const { t } = useTranslation();
  const mine = useMeetings({ guestId: ctx.memberId });
  const hosts = useMeetingHosts(ctx.child.memberId);
  const cancel = useMeetingActions().cancel;
  const [booking, setBooking] = useState<NonNullable<typeof hosts.data>[number]>();
  const list = mine.data ?? [];
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('meetings.title')} />
      <Block>
        <Head title={t('meetings.mine')} count={list.length} />
        {mine.isPending ? (
          <Spinner />
        ) : list.length === 0 ? (
          <p
            className="rounded-[20px] px-4 py-4 text-[14px]"
            style={{ backgroundColor: PAPER, color: INK_SOFT }}
          >
            {t('meetings.noneMine')}
          </p>
        ) : (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2">
            {list.map((s) => (
              <SlotCard
                key={s.id}
                slot={s}
                title={`${dayTitle(kyivDate(s.startsAt))}, ${timeOf(s.startsAt)}`}
                badge={<FormatLabel format={s.format} place={s.place} white />}
                lines={
                  <span className="text-[13.5px] leading-[19px]">
                    <span className="font-medium" style={{ color: INK }}>
                      {s.hostName}
                    </span>
                    <span className="block" style={{ color: INK_SOFT }}>
                      {t('meetings.about', { name: s.studentName ?? '' })}
                      {s.topic ? ` · ${s.topic}` : ''}
                    </span>
                  </span>
                }
                actions={
                  <>
                    <JoinLink slot={s} />
                    {Date.parse(s.startsAt) > Date.now() && (
                      <QuietAction onClick={() => cancel.mutate(s.id)}>
                        {t('meetings.cancel')}
                      </QuietAction>
                    )}
                  </>
                }
              />
            ))}
          </ul>
        )}
      </Block>
      <Block>
        <Head title={t('meetings.bookTitle', { name: ctx.child.firstName })} />
        {hosts.isPending ? (
          <Spinner />
        ) : (
          <ul className="grid gap-x-8 md:grid-cols-2">
            {[...(hosts.data ?? [])]
              .sort((a, b) => b.free_slots - a.free_slots || a.last_name.localeCompare(b.last_name))
              .map((h) => (
                <li
                  key={h.member_id}
                  className="flex min-h-[64px] items-center gap-3 border-t border-[#E4E0D7] py-2.5"
                >
                  <Initials
                    name={`${h.first_name} ${h.last_name}`}
                    tone={h.free_slots > 0 ? 'butter' : 'stone'}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className="block truncate text-[15px] leading-5 font-medium"
                      style={{ color: INK }}
                    >
                      {h.last_name} {h.first_name} {h.middle_name ?? ''}
                    </span>
                    <span className="block truncate text-[12.5px]" style={{ color: INK_SOFT }}>
                      {h.hint}
                    </span>
                  </span>
                  {h.free_slots > 0 ? (
                    <button
                      type="button"
                      className={pillClass('light', 'sm')}
                      onClick={() => setBooking(h)}
                    >
                      {t('meetings.freeSlots', { count: h.free_slots })}
                      <PillIcon icon={CalendarDotsIcon} size="sm" />
                    </button>
                  ) : (
                    <span className="shrink-0 text-[12.5px]" style={{ color: INK_FAINT }}>
                      {t('meetings.noHoursShort')}
                    </span>
                  )}
                </li>
              ))}
          </ul>
        )}
      </Block>
      {booking && <BookSlot ctx={ctx} host={booking} onClose={() => setBooking(undefined)} />}
    </div>
  );
}

/** Meetings teacher–parent: office hours, booking, and the video room of online meetings. */
export function MeetingsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  if (ctx.kind === 'student')
    return (
      <div className="flex flex-col gap-4">
        <PageHead title={t('meetings.title')} />
        <EmptyCard>{t('meetings.forParents')}</EmptyCard>
      </div>
    );
  return ctx.kind === 'guardian' ? (
    <FamilyMeetings key={ctx.key} ctx={ctx} />
  ) : (
    <StaffMeetings key={ctx.key} ctx={ctx} />
  );
}
